/**
 * "Intelligence settings" — the Data Manager rail's eighth button (design §6.5, Task 6).
 *
 * "A minimal 'Intelligence settings' section … a table of config rows per use case (label,
 * key, value input), Save (PATCH), and 'Recompute now' per use case with the run status.
 * Everyone is an administrator this round."
 *
 * `GET {uc}/config` answers a flat `{key: value}` object with no separate label field (the
 * registry's `label`/`label_fr` never reach this route — `apps.intelligence.config.all`
 * returns values only). The "label" column is therefore the key, humanised, rather than a
 * server-supplied string; flagged in the task report as a gap for a later round to close if
 * the settings table is meant to carry the client's own wording rather than the config key.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../components/Btn';
import {
  ApiError,
  fetchIntelConfig,
  fetchIntelUseCases,
  patchIntelConfig,
  queryKeys,
  recomputeIntel,
  type IntelConfig,
  type IntelUseCase,
} from '../../lib/api';
import { toast } from '../../lib/toast';

type JsonKind = 'boolean' | 'number' | 'string' | 'array' | 'object' | 'null';

function jsonKind(value: unknown): JsonKind {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value as JsonKind;
}

/** `mask_threshold` → "Mask threshold" — the key, humanised (see the module note). */
function humanize(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function UseCaseSettings({ useCase }: { useCase: IntelUseCase }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const config = useQuery({
    queryKey: queryKeys.intelConfig(useCase.id),
    queryFn: () => fetchIntelConfig(useCase.id),
  });
  const [draft, setDraft] = useState<IntelConfig>({});
  const [busy, setBusy] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (config.data) setDraft(config.data);
  }, [config.data]);

  // The run status: `usecases[].last_run`, re-fetched every few seconds only while a
  // recompute this panel just triggered is still `queued`/`running` — an ordinary open of
  // the settings tab costs no extra polling.
  const list = useQuery({
    queryKey: queryKeys.intelUseCases(),
    queryFn: () => fetchIntelUseCases(),
    refetchInterval: polling ? 2500 : false,
  });
  const lastRun = list.data?.find((u) => u.id === useCase.id)?.last_run ?? useCase.last_run;

  useEffect(() => {
    if (polling && lastRun && lastRun.status !== 'queued' && lastRun.status !== 'running') {
      setPolling(false);
    }
  }, [polling, lastRun]);

  const setKey = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (!config.data) return;
    // Read-only kinds (object/array) never leave the draft, so they can never be PATCHed —
    // only the primitives a `<input>` actually edited.
    const patch: IntelConfig = {};
    for (const key of Object.keys(draft)) {
      const kind = jsonKind(draft[key]);
      if (kind === 'object' || kind === 'array') continue;
      if (draft[key] !== config.data[key]) patch[key] = draft[key];
    }
    if (!Object.keys(patch).length) return;
    setBusy(true);
    try {
      await patchIntelConfig(useCase.id, patch);
      await qc.invalidateQueries({ queryKey: queryKeys.intelConfig(useCase.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.intelSummary(useCase.id) });
      toast(t('intel.settings.saved'));
    } catch (err) {
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      toast(t('intel.settings.saveFailed', { message }));
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      await recomputeIntel(useCase.id);
      setPolling(true);
      toast(t('intel.settings.recomputeQueued'));
    } catch (err) {
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      toast(t('intel.settings.recomputeFailed', { message }));
    } finally {
      setRecomputing(false);
    }
  };

  const rows = Object.entries(draft);

  return (
    <section className="card mgr-panelcard" data-testid={`intel-settings-${useCase.id}`}>
      <div className="card-head">
        <div className="card-head-text">
          <h3>{useCase.title}</h3>
          <p className="sub">{useCase.question}</p>
        </div>
      </div>
      <div className="result-scroll">
        <table className="mini" data-testid={`intel-config-table-${useCase.id}`}>
          <thead>
            <tr>
              <th>{t('intel.settings.key')}</th>
              <th>{t('intel.settings.value')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, value]) => {
              const kind = jsonKind(value);
              const testId = `intel-config-${useCase.id}-${key}`;
              return (
                <tr key={key}>
                  <td>{humanize(key)}</td>
                  <td>
                    {kind === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={value as boolean}
                        data-testid={testId}
                        onChange={(e) => setKey(key, e.target.checked)}
                      />
                    ) : kind === 'number' ? (
                      <input
                        type="number"
                        value={value as number}
                        data-testid={testId}
                        onChange={(e) => setKey(key, Number(e.target.value))}
                      />
                    ) : kind === 'string' ? (
                      <input
                        type="text"
                        value={value as string}
                        data-testid={testId}
                        onChange={(e) => setKey(key, e.target.value)}
                      />
                    ) : (
                      <pre data-testid={testId}>{JSON.stringify(value)}</pre>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="ad-actions">
        <Btn
          variant="primary"
          size="sm"
          disabled={busy || !config.data}
          onClick={() => void save()}
          data-testid={`intel-save-${useCase.id}`}
        >
          {t('intel.settings.save')}
        </Btn>
        <Btn
          size="sm"
          disabled={recomputing}
          onClick={() => void recompute()}
          data-testid={`intel-recompute-${useCase.id}`}
        >
          {t('intel.settings.recompute')}
        </Btn>
        <span className="sub" data-testid={`intel-runstatus-${useCase.id}`}>
          {lastRun ? t('intel.settings.runStatus', { status: lastRun.status }) : t('intel.settings.noRun')}
        </span>
      </div>
    </section>
  );
}

export function IntelSettings() {
  const { t } = useTranslation();
  const useCases = useQuery({
    queryKey: queryKeys.intelUseCases(),
    queryFn: () => fetchIntelUseCases(),
  });

  return (
    <div data-testid="mgr-intel">
      <header className="card-head-text">
        <h2>{t('intel.settings.title')}</h2>
        <p className="sub">{t('intel.settings.sub')}</p>
      </header>
      {(useCases.data ?? []).map((uc) => (
        <UseCaseSettings key={uc.id} useCase={uc} />
      ))}
    </div>
  );
}

export default IntelSettings;
